import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mvpApi } from '../services/api';
import LanguageSwitch from '../components/LanguageSwitch';
import { SkeletonCaseDetail } from '../components/advisor/SkeletonLoader';
import {
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  isGenderField,
  isMaritalStatusField,
} from '../constants/formOptions';
import CaseAdvisorPicker from '../components/advisor/CaseAdvisorPicker';
import { showPopupMessage } from '../utils/showPopup';
import './AdminCaseDetail.css';

/**
 * Admin case detail: tabular view of all fields (filled + empty) with inline edit.
 * Admin can update any field or fill missing ones; progress updates after save.
 */
export default function AdminCaseDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, sessionId } = useParams();
  const [caseDetail, setCaseDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showReupload, setShowReupload] = useState(false);
  const [reuploadDocType, setReuploadDocType] = useState('');
  const [reuploadReason, setReuploadReason] = useState('');

  const getFieldLabel = (key) => {
    const out = String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    const tr = t(`fields.${key}`);
    return tr !== `fields.${key}` ? tr : out;
  };

  /** Status label from i18n; key follows admin.status{Status} (e.g. admin.statusActive). */
  const getStatusLabel = (status) => {
    if (!status) return '-';
    const key = `admin.status${status.charAt(0).toUpperCase()}${status.slice(1)}`;
    const label = t(key);
    return label !== key ? label : status;
  };

  useEffect(() => {
    if (userId && sessionId) loadCaseDetail();
  }, [userId, sessionId]);

  const loadCaseDetail = async (opts = {}) => {
    const silent = Boolean(opts.silent);
    try {
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }
      const response = await mvpApi.getUserCaseDetail(parseInt(userId, 10), sessionId);
      if (response.data?.case) setCaseDetail(response.data.case);
    } catch (err) {
      console.error('Error loading case:', err);
      if (!silent) setError(t('admin.caseDetail.loadError', 'Failed to load case details.'));
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const handleSaveField = async () => {
    if (editingField == null || !userId || !sessionId) return;
    try {
      setIsSaving(true);
      await mvpApi.updateCaseFields(parseInt(userId, 10), sessionId, { [editingField]: editValue });
      await loadCaseDetail();
      setEditingField(null);
      setEditValue('');
    } catch (err) {
      console.error('Error updating field:', err);
      const msg = err.response?.data?.detail?.error || err.response?.data?.detail || t('admin.caseDetail.updateError', 'Failed to update field.');
      showPopupMessage(typeof msg === 'string' ? msg : t('admin.caseDetail.updateError', 'Failed to update field.'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRequestReupload = async () => {
    if (!reuploadDocType?.trim()) {
      showPopupMessage(t('admin.caseDetail.selectDocType', 'Please select a document type'), 'error');
      return;
    }
    try {
      await mvpApi.requestDocumentReupload(
        parseInt(userId, 10),
        sessionId,
        reuploadDocType.trim(),
        reuploadReason.trim()
      );
      showPopupMessage(t('admin.caseDetail.reuploadSent', 'Reupload request sent to user.'), 'success');
      setShowReupload(false);
      setReuploadDocType('');
      setReuploadReason('');
      await loadCaseDetail();
    } catch (err) {
      console.error('Error requesting reupload:', err);
      showPopupMessage(err.response?.data?.detail || t('admin.caseDetail.reuploadFailed', 'Failed to send reupload request.'), 'error');
    }
  };

  const allFields = useMemo(() => {
    if (!caseDetail) return [];
    const collected = caseDetail.collected_fields || {};
    const missing = caseDetail.missing_fields || [];
    const keys = [...new Set([...Object.keys(collected), ...missing])];
    return keys.sort((a, b) => {
      const aFilled = collected[a] != null && String(collected[a]).trim() !== '';
      const bFilled = collected[b] != null && String(collected[b]).trim() !== '';
      if (aFilled !== bFilled) return aFilled ? -1 : 1;
      return a.localeCompare(b);
    });
  }, [caseDetail]);

  const collected = caseDetail?.collected_fields || {};
  const completion = caseDetail?.completion_percentage ?? 0;
  const isSignature = (v) => typeof v === 'string' && v.startsWith('data:image');

  if (isLoading && !location.state?.userName) {
    return <SkeletonCaseDetail />;
  }

  if (error || !caseDetail) {
    return (
      <div className="admin-case-page">
        <header className="admin-case-header">
          <button type="button" className="admin-case-back" onClick={() => navigate('/advisor')}>← {t('back')}</button>
          <h1 className="admin-case-title">{t('admin.caseDetails')}</h1>
          <LanguageSwitch />
        </header>
        <p className="admin-case-error">{error || t('admin.caseDetail.notFound', 'Case not found.')}</p>
      </div>
    );
  }

  return (
    <div className="admin-case-page admin-case-page--light">
      <header className="admin-case-header">
        <button type="button" className="admin-case-back" onClick={() => navigate('/advisor')}>
          ← {t('back')}
        </button>
        <h1 className="admin-case-title">{t('admin.caseDetails')}</h1>
        <LanguageSwitch />
      </header>

      <main className="admin-case-main">
        <div className="admin-case-heading">
          <h2>Case workspace</h2>
          <p>Review and update all extracted fields for this application.</p>
        </div>

        {isLoading ? (
          <SkeletonCaseDetailContent />
        ) : (
          <>
            <div className="admin-case-summary">
              <div className="admin-case-summary-row">
                <span className="admin-case-summary-label">{t('admin.caseDetail.user')}</span>
                <span className="admin-case-summary-value">
                  {caseDetail?.user_name ?? location.state?.userName ?? '-'} · {caseDetail?.user_email ?? '-'}
                </span>
              </div>
              <div className="admin-case-summary-row">
                <span className="admin-case-summary-label">{t('admin.caseDetail.session')}</span>
                <span className="admin-case-summary-value admin-case-session-id">{caseDetail?.session_id || sessionId}</span>
              </div>
              <div className="admin-case-summary-row">
                <span className="admin-case-summary-label">{t('admin.caseDetail.status')}</span>
                <span className={`admin-case-status admin-case-status--${(caseDetail?.status || '').toLowerCase()}`}>
                  {getStatusLabel(caseDetail?.status)}
                </span>
              </div>
              <div className="admin-case-summary-row">
                <span className="admin-case-summary-label">
                  {t('admin.caseDetail.assignedAdvisor', 'Assigned advisor')}
                </span>
                <span className="admin-case-summary-value">
                  <CaseAdvisorPicker
                    userId={parseInt(userId, 10)}
                    sessionId={sessionId}
                    assignedAdvisorId={caseDetail?.assigned_advisor_id}
                    assignedAdvisorName={caseDetail?.assigned_advisor_name}
                    onAfterSuccess={() => loadCaseDetail({ silent: true })}
                    onNotify={(msg) => showPopupMessage(msg, 'error')}
                  />
                </span>
              </div>
              <div className="admin-case-summary-row admin-case-progress-row">
                <span className="admin-case-summary-label">{t('admin.caseDetail.completion')}</span>
                <div className="admin-case-progress-wrap">
                  <div className="admin-case-progress-bar">
                    <div className="admin-case-progress-fill" style={{ width: `${Math.min(100, Math.max(0, completion))}%` }} />
                  </div>
                  <span className="admin-case-progress-pct">{Math.round(completion)}%</span>
                </div>
              </div>
            </div>

            <section className="admin-case-table-section">
              <h2 className="admin-case-table-title">{t('admin.caseDetail.allFields')}</h2>
              <p className="admin-case-table-hint">{t('admin.caseDetail.editHint')}</p>
              <div className="admin-case-table-wrap">
                <table className="admin-case-table">
                  <thead>
                    <tr>
                      <th>{t('admin.caseDetail.field')}</th>
                      <th>{t('admin.caseDetail.value')}</th>
                      <th className="admin-case-th-action">{t('admin.caseDetail.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allFields.map((key) => {
                      const value = collected[key];
                      const filled = value != null && String(value).trim() !== '';
                      const isEdit = editingField === key;
                      return (
                        <tr key={key} className={filled ? 'admin-case-row--filled' : 'admin-case-row--missing'}>
                          <td className="admin-case-cell-label">{getFieldLabel(key)}</td>
                          <td className="admin-case-cell-value">
                            {isEdit ? (
                              isGenderField(key) ? (
                                <select
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveField();
                                    if (e.key === 'Escape') { setEditingField(null); setEditValue(''); }
                                  }}
                                  className="admin-case-input admin-case-select"
                                  autoFocus
                                >
                                  <option value="">-</option>
                                  {GENDER_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              ) : isMaritalStatusField(key) ? (
                                <select
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveField();
                                    if (e.key === 'Escape') { setEditingField(null); setEditValue(''); }
                                  }}
                                  className="admin-case-input admin-case-select"
                                  autoFocus
                                >
                                  <option value="">-</option>
                                  {MARITAL_STATUS_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveField();
                                    if (e.key === 'Escape') { setEditingField(null); setEditValue(''); }
                                  }}
                                  className="admin-case-input"
                                  autoFocus
                                />
                              )
                            ) : filled ? (
                              isSignature(value) ? (
                                <span className="admin-case-signature-preview">
                                  <img src={value} alt="" /> {t('dataPanel.signatureCaptured')}
                                </span>
                              ) : (
                                <span className="admin-case-value-text">{String(value)}</span>
                              )
                            ) : (
                              <span className="admin-case-empty">{t('admin.caseDetail.empty')}</span>
                            )}
                          </td>
                          <td className="admin-case-cell-action">
                            {isEdit ? (
                              <div className="admin-case-inline-actions">
                                <button type="button" className="admin-case-btn-save" onClick={handleSaveField} disabled={isSaving}>
                                  {isSaving ? '…' : t('admin.caseDetail.save')}
                                </button>
                                <button type="button" className="admin-case-btn-cancel" onClick={() => { setEditingField(null); setEditValue(''); }} disabled={isSaving}>
                                  {t('dataPanel.cancel')}
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="admin-case-btn-edit"
                                onClick={() => { setEditingField(key); setEditValue(value != null ? String(value) : ''); }}
                              >
                                {filled ? t('admin.caseDetail.edit') : t('admin.caseDetail.fill')}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
        <section className="admin-case-actions-section">
          <button type="button" className="admin-case-btn-reupload" onClick={() => setShowReupload(true)}>
            {t('admin.requestReupload')}
          </button>
          {showReupload && (
            <div className="admin-case-reupload-card">
              <h3>{t('admin.caseDetail.requestReupload')}</h3>
              <input
                type="text"
                placeholder={t('admin.caseDetail.documentTypePlaceholder')}
                value={reuploadDocType}
                onChange={(e) => setReuploadDocType(e.target.value)}
                className="admin-case-input"
              />
              <input
                type="text"
                placeholder={t('admin.caseDetail.reasonPlaceholder')}
                value={reuploadReason}
                onChange={(e) => setReuploadReason(e.target.value)}
                className="admin-case-input"
              />
              <div className="admin-case-reupload-actions">
                <button type="button" className="admin-case-btn-primary" onClick={handleRequestReupload}>
                  {t('admin.caseDetail.send')}
                </button>
                <button type="button" className="admin-case-btn-cancel" onClick={() => { setShowReupload(false); setReuploadDocType(''); setReuploadReason(''); }}>
                  {t('dataPanel.cancel')}
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
